import type { CheckoutLineDetailsFragment, OrderLineFragment } from '@boc/framework/saleor-graphql';
import { parseImageMetadata } from './types';
import { parseMapPuzzleMetadata } from '~/local/custom-map-puzzle/types';

/**
 * Gets the display image URL for a line item, checking for custom puzzle metadata first
 */
export function getLineItemDisplayImage(
  line: CheckoutLineDetailsFragment | OrderLineFragment
): string | undefined {
  // Check if this is a map puzzle
  const mapPuzzleMeta = line.metadata?.find((meta) => meta.key === 'mapPuzzle');
  if (mapPuzzleMeta) {
    const mapMetadata = parseMapPuzzleMetadata(mapPuzzleMeta.value);
    if (mapMetadata?.previewUrl) {
      return mapMetadata.previewUrl;
    }
  }

  // Check if this is a custom puzzle with image metadata
  const imageMeta = line.metadata?.find((meta) => meta.key === 'imageUrl');

  if (imageMeta) {
    const imageMetadata = parseImageMetadata(imageMeta.value);
    if (imageMetadata?.croppedUrl) {
      return imageMetadata.croppedUrl;
    }
  }

  // Fallback to product thumbnail
  return line.variant?.product.thumbnail?.url;
}

/**
 * Checks if a line item is a custom puzzle
 */
export function isCustomPuzzleLineItem(
  line: CheckoutLineDetailsFragment | OrderLineFragment
): boolean {
  const imageMeta = line.metadata?.find((meta) => meta.key === 'imageUrl');
  return imageMeta ? !!parseImageMetadata(imageMeta.value) : false;
}

/**
 * Checks if a line item is a map puzzle
 */
export function isMapPuzzleLineItem(
  line: CheckoutLineDetailsFragment | OrderLineFragment
): boolean {
  const mapPuzzleMeta = line.metadata?.find((meta) => meta.key === 'mapPuzzle');
  return !!mapPuzzleMeta;
}

/**
 * Checks if the checkout holds a custom or map puzzle line
 */
export function hasCustomPuzzleLine(lines: readonly CheckoutLineDetailsFragment[]): boolean {
  return lines.some((line) => isCustomPuzzleLineItem(line) || isMapPuzzleLineItem(line));
}

const PICKUP_METHOD = /pick\s*-?\s*up|collect/i;

/**
 * Checks if a shipping method is the "Store Pickup" method set up in Saleor,
 * which is not offered for custom puzzles
 */
export function isPickupShippingMethod(method: { name: string }): boolean {
  return PICKUP_METHOD.test(method.name);
}

type PuzzleAttributes = readonly {
  attribute: { slug?: string | null };
  values: readonly { name?: string | null }[];
}[];

/** The product's `puzzle-shape` attribute, e.g. "Circle" */
export function getPuzzleShape(attributes: PuzzleAttributes): string | undefined {
  return attributes.find((attr) => attr.attribute.slug === 'puzzle-shape')?.values[0]?.name ?? undefined;
}

/**
 * Circle puzzles are printed from a square image and die cut round, so the
 * crop stays square but is shown as the circle that survives the cut.
 */
export function isCirclePuzzle(attributes: PuzzleAttributes): boolean {
  return getPuzzleShape(attributes)?.toLowerCase() === 'circle';
}

const SHAPE_ORDER = ['Circle', 'Rectangle', 'Square'];

/**
 * Groups puzzles by shape for the size picker: circle, rectangle, square, then
 * any other shape alphabetically. Products keep their order within a group.
 */
export function groupPuzzlesByShape<T extends { attributes: PuzzleAttributes }>(
  products: readonly T[]
): { shape: string; products: T[] }[] {
  const groups = new Map<string, T[]>();
  for (const product of products) {
    const shape = getPuzzleShape(product.attributes) ?? 'Other';
    groups.set(shape, [...(groups.get(shape) ?? []), product]);
  }

  const rank = (shape: string) => {
    const index = SHAPE_ORDER.indexOf(shape);
    return index === -1 ? SHAPE_ORDER.length : index;
  };

  return [...groups.entries()]
    .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
    .map(([shape, grouped]) => ({ shape, products: grouped }));
}
